import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);          // { scope, user?, org?, username? }
  const [loading, setLoading] = useState(true);
  // Phase L — legal consent state. Populated from /auth/login response;
  // when `terms_outdated` or `privacy_outdated` is true the dashboard
  // mounts a blocking re-consent modal until the user accepts.
  const [legal, setLegal] = useState({
    terms_outdated: false,
    privacy_outdated: false,
    terms_current: 0,
    privacy_current: 0,
  });

  const reload = useCallback(async () => {
    const token = localStorage.getItem("elswitch_token");
    if (!token) {
      setLoading(false);
      setUser(null);
      return;
    }
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
    } catch {
      localStorage.removeItem("elswitch_token");
      localStorage.removeItem("elswitch_scope");
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const login = async (emailOrUsername, password, otpCode) => {
    const r = await api.post("/auth/login", {
      email_or_username: emailOrUsername,
      password,
      otp_code: otpCode || undefined,
    });
    localStorage.setItem("elswitch_token", r.data.access_token);
    localStorage.setItem("elswitch_scope", r.data.scope);
    if (r.data.role) localStorage.setItem("elswitch_role", r.data.role);
    if (r.data.org_id) localStorage.setItem("elswitch_org_id", r.data.org_id);
    setLegal({
      terms_outdated:   !!r.data.terms_outdated,
      privacy_outdated: !!r.data.privacy_outdated,
      terms_current:    r.data.terms_current || 0,
      privacy_current:  r.data.privacy_current || 0,
    });
    await reload();
    return r.data;
  };

  const logout = () => {
    localStorage.removeItem("elswitch_token");
    localStorage.removeItem("elswitch_scope");
    localStorage.removeItem("elswitch_role");
    localStorage.removeItem("elswitch_org_id");
    setUser(null);
    setLegal({
      terms_outdated: false, privacy_outdated: false,
      terms_current: 0, privacy_current: 0,
    });
    window.location.href = "/login";
  };

  // Phase L — called by LegalConsentModal after the user accepts. Clears
  // the outdated flags so the modal unmounts and the dashboard becomes
  // interactive again.
  const acknowledgeLegal = useCallback((versions) => {
    setLegal({
      terms_outdated: false,
      privacy_outdated: false,
      terms_current:   versions?.terms_current   || 0,
      privacy_current: versions?.privacy_current || 0,
    });
  }, []);

  const scope = user?.scope;
  const role = user?.user?.role;
  const org = user?.org;
  const username = user?.scope === "platform" ? user?.username : user?.user?.email;
  const isPending = org?.status && org.status !== "active";

  return (
    <AuthContext.Provider
      value={{
        user, loading, scope, role, org, username, isPending,
        login, logout, setUser, reload,
        legal, acknowledgeLegal,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
